// Tradução do que o SO de saúde devolve → as linhas que o VYV grava.
//
// Módulo PURO e sem imports, de propósito (mesmo padrão de pageRows.ts /
// chunkIds.ts / waComeback.ts): é a única parte do sync nativo que dá para
// testar sem device, sem plugin e sem Supabase. Todo o resto de
// `useConnections.connectHealth/syncHealth` é encanamento em volta destas
// funções.
//
// De onde vem o dado: `@capgo/capacitor-health`, que é a MESMA API para
// HealthKit (iOS) e Health Connect (Android) — o plugin roteia para a ponte
// nativa certa. Por isso aqui não existe ramo por plataforma: só o `source`
// muda ('apple_health' | 'health_connect'), e ele entra como parâmetro.
//
// O ponto do produto: esse hub do sistema operacional já recebe Apple Watch,
// Oura, Fitbit, WHOOP e Garmin. Ler dele é ler todos eles de uma vez — é o que
// torna integração direta com cada wearable desnecessária.

export type HealthProvider = "apple_health" | "health_connect";

/** Estados de sono do plugin. 'inBed' NÃO é sono — é tempo deitado. */
export type SleepState = "inBed" | "asleep" | "awake" | "rem" | "deep" | "light";

export interface RawSleepSample {
  startDate: string;
  endDate: string;
  sleepState?: SleepState;
}

export interface RawWorkout {
  workoutType: string;
  /** Segundos. */
  duration: number;
  totalEnergyBurned?: number;
  /** Metros. */
  totalDistance?: number;
  startDate: string;
  endDate: string;
  sourceName?: string;
  /** UUID do HealthKit / id de metadata do Health Connect. */
  platformId?: string;
}

export interface RawAggregatedSample {
  startDate: string;
  endDate: string;
  value: number;
  unit?: string;
}

export interface SleepRow {
  user_id: string;
  sleep_date: string;
  bedtime: string;
  wakeup: string;
  duration_hours: number;
  score: number | null;
  quality: string | null;
  source: HealthProvider;
  external_id: string;
}

export interface WorkoutRow {
  user_id: string;
  name: string;
  exercises: { type: string; distance_meters: number | null; average_speed_mps: number | null }[];
  started_at: string;
  completed_at: string;
  duration_minutes: number;
  calories_burned: number | null;
  source: HealthProvider;
  external_id: string;
}

export interface MetricRow {
  user_id: string;
  metric_type: string;
  value: number;
  unit: string;
  /**
   * INÍCIO da janela (migration 20260803160000). Para amostra pontual é o
   * instante dela. O nome é impreciso e a dívida está assumida na migration:
   * renomear para `started_at` exigiria tocar spotify-pull, que escreve nesta
   * coluna e está no cron.
   */
  recorded_at: string;
  /** FIM da janela. `null` significa amostra pontual, não "desconhecido". */
  ended_at: string | null;
  source: HealthProvider;
  external_id: string;
  metadata: Record<string, unknown> | null;
}

// ── Datas ────────────────────────────────────────────────────────────────

/**
 * Data local no formato YYYY-MM-DD. Usa os getters LOCAIS do Date, nunca
 * toISOString(): `sleep_date` é coluna `date` e a tela lê como dia do usuário.
 * Converter por UTC empurra toda noite que começa depois das 21h (BRT) para o
 * dia seguinte — o bug de fuso que o datetimeNatural.test.ts já pegou uma vez
 * neste repo.
 */
export function localDayString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return y + "-" + m + "-" + day;
}

// ── Dia civil num FUSO EXPLÍCITO ──────────────────────────────────────────
//
// `localDayString` acima usa o fuso do APARELHO. Para o dia do agregado isso
// não serve: o dia do VYV é o dia civil do USUÁRIO, que está gravado em
// `user_whisper_preferences.timezone`. Aparelho em fuso diferente do perfil
// (viagem, tablet configurado errado, emulador em UTC) rotularia o agregado
// num dia que não é o do usuário — e como a data entra no `external_id`, que
// é a chave de dedup, o erro fica gravado na identidade da linha.

/** Offset do fuso `tz` NO INSTANTE `d`, em ms. Positivo a leste de Greenwich. */
function zoneOffsetMs(d: Date, tz: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(d);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  // `hour` volta "24" para meia-noite em algumas versões do ICU — daí o % 24.
  const asIfUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute"),
    get("second"),
  );
  return asIfUtc - Math.floor(d.getTime() / 1000) * 1000;
}

/**
 * Dia civil YYYY-MM-DD do instante `d` no fuso `tz`.
 * `en-CA` porque é o locale cujo formato numérico curto JÁ é YYYY-MM-DD —
 * montar à mão a partir de `formatToParts` daria o mesmo com mais código.
 */
export function dayStringInZone(d: Date, tz: string): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch {
    // Fuso inválido vindo do banco não pode derrubar o sync: cai no aparelho,
    // que é o comportamento de antes desta função existir.
    console.error("[healthMap] fuso inválido, usando o do aparelho:", tz);
    return localDayString(d);
  }
}

/**
 * Instante UTC da MEIA-NOITE do dia civil que contém `d` no fuso `tz`.
 *
 * É isto que conserta o bug de 04/08: o bucket do plugin começa no início da
 * JANELA DE CONSULTA (`windowStart + n·24h`), nunca na meia-noite. Snapar para
 * cá torna `recorded_at` um limite de dia de verdade e `external_id` o dia
 * certo, independente da hora em que o sync rodou.
 *
 * Duas passadas por causa do horário de verão: a primeira estimativa usa o
 * offset da meia-noite-como-se-fosse-UTC, que na noite da virada pode ser o
 * offset do dia anterior. A segunda recalcula no instante já corrigido.
 */
export function startOfDayInZoneUtc(d: Date, tz: string): Date {
  let dayStr: string;
  try {
    dayStr = dayStringInZone(d, tz);
    const probe = zoneOffsetMs(d, tz);
    if (!Number.isFinite(probe)) throw new Error("offset inválido");
  } catch {
    // Fuso inválido: meia-noite do aparelho, mesmo fallback do dayStringInZone.
    const local = new Date(d.getTime());
    local.setHours(0, 0, 0, 0);
    return local;
  }
  const [y, m, day] = dayStr.split("-").map(Number);
  const asUtcMidnight = Date.UTC(y, m - 1, day, 0, 0, 0, 0);
  const off1 = zoneOffsetMs(new Date(asUtcMidnight), tz);
  let ms = asUtcMidnight - off1;
  const off2 = zoneOffsetMs(new Date(ms), tz);
  if (off2 !== off1) ms = asUtcMidnight - off2;
  // Some historical DST transitions skipped 00:00 entirely (Brazil 2018).
  // If the offset approximation crossed into the preceding civil day, find
  // the earliest real instant of the requested day rather than relabel it.
  if (dayStringInZone(new Date(ms), tz) !== dayStr || dayStringInZone(new Date(ms - 1), tz) === dayStr) {
    let low = asUtcMidnight - 36 * 3_600_000, high = asUtcMidnight + 36 * 3_600_000;
    while (high - low > 1) {
      const middle = Math.floor((low + high) / 2);
      if (dayStringInZone(new Date(middle), tz) < dayStr) low = middle;
      else high = middle;
    }
    if (dayStringInZone(new Date(high), tz) !== dayStr) throw new Error("civil day does not exist in timezone");
    ms = high;
  }
  return new Date(ms);
}

// ── Janela de leitura ─────────────────────────────────────────────────────

/**
 * Quantos dias para trás a leitura cobre. 30 dias cobre o teto padrão do
 * Health Connect sem a permissão de histórico e é o suficiente para as telas
 * (a de sono mostra 7 dias, a de métricas 30).
 */
export const HEALTH_DAYS_BACK = 30;

/**
 * Sobreposição da janela incremental, em dias.
 *
 * A marca d'água diz até onde já lemos, mas reler a partir DELA seria errado:
 * a plataforma revisa amostras depois do fato (o relógio sincroniza tarde, o
 * usuário corrige uma noite no app do sistema, um app terceiro grava com
 * atraso). Relemos alguns dias para trás da marca para essas revisões
 * entrarem. Isso não duplica nada: a chave natural por origem
 * (user_id, source, external_id) faz o re-upsert virar no-op.
 */
export const HEALTH_WATERMARK_OVERLAP_DAYS = 3;

/**
 * Teto do buraco. Se o usuário passa meses sem abrir, a marca d'água fica
 * velha e a janela cresceria sem limite.
 *
 * 91, não 90, desde o alinhamento à meia-noite (04/08): o snap acrescenta até
 * 24h e isso é CORREÇÃO, não folga — janela de N dias começando às 19:07
 * cobria N−1 dias civis inteiros mais dois pedaços. O teto é guarda-corpo
 * contra leitura ilimitada, não contrato de produto.
 */
export const HEALTH_MAX_WINDOW_DAYS = 91;

export interface HealthWindowOptions {
  /** Instante de referência (fim da janela). */
  now: Date;
  /** Fuso do usuário — `user_whisper_preferences.timezone`. */
  tz: string;
  daysBack?: number;
  overlapDays?: number;
  maxWindowDays?: number;
}

/**
 * Início da janela de leitura para um tipo, a partir da marca d'água dele.
 *
 * Extraída de `syncHealth` (era um closure) porque é METADE do fix de 04/08 —
 * a metade que conserta o VALOR, não o rótulo — e dentro do hook não havia
 * como verificá-la sem device. O snap em `buildMetricRows` corrige o dia que a
 * linha declara; este corrige o que entra na soma.
 *
 * POR JANELA DE DATAS, nunca "desde o último sucesso": se o usuário passa duas
 * semanas sem abrir, "desde o último sucesso" deixaria um buraco que nunca
 * mais fecha.
 *
 * ⚠️ O QUE A MARCA FAZ DE VERDADE — medido, e é o OPOSTO do que o comentário
 * original desta função (escrito por mim no PR de 03/08) afirmava. Ele dizia
 * "a marca só ENCOLHE a janela quando o sync é recente". Não encolhe:
 *
 *   `Math.min(fromMark, defaultStart)` escolhe o MAIS ANTIGO dos dois. Com
 *   marca de ontem, `fromMark` é 3 dias atrás e `defaultStart` é 30 — o min
 *   pega 30. A janela é 30 dias em TODO sync com marca fresca; a marca só
 *   ALARGA, e só quando está velha o bastante para `fromMark` passar de
 *   `defaultStart`, aí limitada pelo teto.
 *
 * Traçado com `now = 2026-08-04T19:07Z`:
 *   marca 03/08 → janela 30 dias   (fromMark 31/07, ignorado)
 *   marca 20/07 → janela 30 dias   (fromMark 17/07, ignorado)
 *   marca 2019  → janela 91 dias   (teto)
 *
 * O comportamento é SEGURO — ler demais nunca cria buraco, e uma marca
 * corrompida não consegue estreitar a leitura. O que ele não entrega é a
 * economia que justificava a marca ("não reler tudo"): para sync frequente,
 * `HEALTH_WATERMARK_OVERLAP_DAYS` é inerte. Trocar o `min` por `max` daria a
 * economia e abriria a porta para marca ruim virar buraco — é decisão de
 * produto sobre volume de leitura, não conserto óbvio, então fica registrada
 * aqui em vez de mudada de lado.
 *
 * ⚠️ O RESULTADO É SEMPRE MEIA-NOITE LOCAL. É isto que faz
 * `queryAggregated({ bucket: "day" })` produzir dias civis: o plugin fatia a
 * partir do início da janela em passos de 24h, então início não alinhado =
 * buckets não alinhados = a soma de uma janela móvel atribuída a um dia civil.
 */
export function healthWindowStart(
  mark: string | null | undefined,
  o: HealthWindowOptions,
): string {
  const daysBack = o.daysBack ?? HEALTH_DAYS_BACK;
  const overlap = o.overlapDays ?? HEALTH_WATERMARK_OVERLAP_DAYS;
  const maxDays = o.maxWindowDays ?? HEALTH_MAX_WINDOW_DAYS;
  const nowMs = o.now.getTime();
  const markMs = mark ? Date.parse(mark) : NaN;
  const defaultStart = nowMs - daysBack * 86_400_000;
  const floor = nowMs - maxDays * 86_400_000;
  const raw = !Number.isFinite(markMs)
    ? defaultStart
    : Math.max(floor, Math.min(markMs - overlap * 86_400_000, defaultStart));
  // O snap só ALARGA (meia-noite ≤ instante). Truncar de volta para caber no
  // teto reintroduziria o dia parcial que o snap existe para corrigir — por
  // isso o teto subiu para 91 em vez de o snap ser limitado.
  return startOfDayInZoneUtc(new Date(raw), o.tz).toISOString();
}

/** HH:MM:SS local — o formato que a coluna `time` de sleep_logs espera. */
export function localTimeString(d: Date, tz?: string): string {
  if (tz) {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(d);
    const value = (key: string) => parts.find(part => part.type === key)?.value ?? "00";
    return String(Number(value("hour")) % 24).padStart(2, "0") + ":" + value("minute") + ":" + value("second");
  }
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  return h + ":" + m + ":" + s;
}

/**
 * A que NOITE um sample pertence.
 *
 * Convenção do VYV (a mesma do fitbit-sync e do useSleep): a noite é rotulada
 * pelo dia em que a pessoa ACORDA. Quem dorme 23h30 de 01/01 e acorda 07h de
 * 02/01 registrou a noite de 02/01.
 *
 * Implementação: deslocar 6h e pegar o dia local. É monótona e cobre os quatro
 * casos sem `if` por hora:
 *   23:00 dia 1 → 05:00 dia 2 → dia 2   (dormiu antes da meia-noite)
 *   02:00 dia 2 → 08:00 dia 2 → dia 2   (dormiu depois da meia-noite)
 *   06:30 dia 2 → 12:30 dia 2 → dia 2   (último trecho antes de acordar)
 *   13:00 dia 1 → 19:00 dia 1 → dia 1   (cochilo da tarde fica no próprio dia)
 */
export function nightDateOf(startIso: string, tz?: string): string {
  const t = new Date(startIso).getTime();
  return tz ? dayStringInZone(new Date(t + 6 * 3600 * 1000), tz) : localDayString(new Date(t + 6 * 3600 * 1000));
}

// Estados que contam como sono de verdade. 'inBed' e 'awake' ficam de fora:
// são o denominador da eficiência, não o numerador.
const ASLEEP_STATES = new Set<string>(["asleep", "rem", "deep", "light"]);

/**
 * Duração da UNIÃO de intervalos, em ms. Trecho coberto por dois samples conta
 * uma vez só.
 */
export function unionDurationMs(intervals: [number, number][]): number {
  if (intervals.length === 0) return 0;
  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  let total = 0;
  let [curStart, curEnd] = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    const [s, e] = sorted[i];
    if (s > curEnd) {
      total += curEnd - curStart;
      curStart = s;
      curEnd = e;
    } else if (e > curEnd) {
      curEnd = e;
    }
  }
  total += curEnd - curStart;
  return total;
}

/**
 * Eficiência (0–100) → enum `quality` do sleep_logs.
 * Mesmos cortes do fitbit-sync/oura-sync para o badge da UI não mudar de
 * significado conforme a origem do dado.
 */
export function efficiencyToQuality(score: number | null): string | null {
  if (typeof score !== "number" || !Number.isFinite(score)) return null;
  if (score >= 85) return "amazing";
  if (score >= 70) return "good";
  if (score >= 55) return "fair";
  if (score >= 40) return "poor";
  return "terrible";
}

// ── Sono ─────────────────────────────────────────────────────────────────

/**
 * Agrupa os samples de sono em UMA linha por noite.
 *
 * O HealthKit devolve vários samples por noite (um por transição de estágio),
 * não uma sessão fechada — por isso a agregação acontece aqui e não no
 * chamador.
 *
 * - bedtime  = início do primeiro sample da noite
 * - wakeup   = fim do último
 * - duration = soma só dos trechos DORMINDO (rem+deep+light+asleep)
 * - score    = eficiência = dormindo / (deitado) × 100
 *
 * Quando nenhum sample traz `sleepState` (provider que só reporta "esteve na
 * cama"), duração cai para o span total e score fica null em vez de fingir
 * 100% — número inventado em tela de saúde é pior que número ausente.
 */
export function buildSleepRows(
  samples: RawSleepSample[],
  userId: string,
  source: HealthProvider,
  tz?: string,
): SleepRow[] {
  const byNight = new Map<string, RawSleepSample[]>();
  for (const s of samples) {
    if (!s || typeof s.startDate !== "string" || typeof s.endDate !== "string") continue;
    const start = new Date(s.startDate).getTime();
    const end = new Date(s.endDate).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;
    const night = nightDateOf(s.startDate, tz);
    const list = byNight.get(night);
    if (list) list.push(s);
    else byNight.set(night, [s]);
  }

  const rows: SleepRow[] = [];
  for (const [night, list] of byNight) {
    list.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

    const first = new Date(list[0].startDate);
    let lastEndMs = 0;
    let sawState = false;

    // ⚠️ UNIÃO DE INTERVALOS, não soma de durações. O HealthKit devolve
    // camadas SOBREPOSTAS para a mesma noite: um sample 'inBed' cobrindo as
    // 8h inteiras MAIS um sample por estágio dentro dela — e, se a pessoa usa
    // relógio e celular, às vezes duas fontes escrevendo a mesma noite.
    //
    // Somar span a span contava a noite duas vezes: `inBedMs` virava 16h para
    // 8h reais e a eficiência caía para ~50% numa noite perfeita. O número
    // errado seria plausível na tela — é o pior tipo de bug em dado de saúde,
    // porque ninguém desconfia de "eficiência 52%".
    const asleepIntervals: [number, number][] = [];
    const bedIntervals: [number, number][] = [];

    for (const s of list) {
      const start = new Date(s.startDate).getTime();
      const end = new Date(s.endDate).getTime();
      if (end > lastEndMs) lastEndMs = end;
      bedIntervals.push([start, end]);
      if (s.sleepState) {
        sawState = true;
        if (ASLEEP_STATES.has(s.sleepState)) asleepIntervals.push([start, end]);
      }
    }

    const asleepMs = unionDurationMs(asleepIntervals);
    const inBedMs = unionDurationMs(bedIntervals);

    // Sem estágio nenhum: o tempo total é a melhor estimativa disponível.
    const effectiveAsleepMs = sawState ? asleepMs : inBedMs;
    if (effectiveAsleepMs <= 0) continue; // noite só de 'awake' não vira linha

    const durationHours = Math.round((effectiveAsleepMs / 3_600_000) * 100) / 100;
    const score = sawState && inBedMs > 0
      ? Math.max(0, Math.min(100, Math.round((asleepMs / inBedMs) * 100)))
      : null;

    rows.push({
      user_id: userId,
      sleep_date: night,
      bedtime: localTimeString(first, tz),
      wakeup: localTimeString(new Date(lastEndMs), tz),
      duration_hours: durationHours,
      score: score,
      quality: efficiencyToQuality(score),
      source: source,
      // A noite é a chave natural (sleep_logs tem UNIQUE (user_id,
      // sleep_date)). Usar a data e não o platformId do primeiro sample deixa
      // o external_id estável mesmo quando o SO reescreve os samples da noite
      // — que é o caso normal quando o relógio sincroniza depois.
      external_id: night,
    });
  }

  rows.sort((a, b) => (a.sleep_date < b.sleep_date ? -1 : 1));
  return rows;
}

// ── Treinos ──────────────────────────────────────────────────────────────

/**
 * camelCase do plugin → rótulo legível.
 * 'highIntensityIntervalTraining' → 'High Intensity Interval Training'.
 * Sem tabela de tradução de propósito: são ~130 tipos de workout e traduzir
 * todos criaria 390 chaves de i18n para um rótulo que o usuário pode editar.
 */
export function humanizeWorkoutType(t: string): string {
  if (!t) return "Workout";
  const spaced = t.replace(/([A-Z])/g, " $1").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Workouts do SO → linhas de `workout_sessions` na forma ACTIVITY.
 *
 * A forma importa: `workout_sessions.exercises` carrega dois formatos
 * incompatíveis na mesma coluna (ver src/lib/fitness/sessionKind.ts) — gym
 * (`[{name, sets}]`) e activity (`[{type, distance_meters,
 * average_speed_mps}]`). Import externo é SEMPRE activity, igual ao Strava;
 * escrever na forma gym faria a tela contar séries que não existem e mostrar
 * volume zero.
 */
export function buildWorkoutRows(
  workouts: RawWorkout[],
  userId: string,
  source: HealthProvider,
): WorkoutRow[] {
  const rows: WorkoutRow[] = [];
  for (const w of workouts) {
    if (!w || typeof w.startDate !== "string") continue;
    const startMs = new Date(w.startDate).getTime();
    if (!Number.isFinite(startMs)) continue;

    const durationSec = typeof w.duration === "number" && Number.isFinite(w.duration) ? w.duration : 0;
    const endIso = typeof w.endDate === "string" && Number.isFinite(new Date(w.endDate).getTime())
      ? new Date(w.endDate).toISOString()
      : new Date(startMs + durationSec * 1000).toISOString();

    const distance = typeof w.totalDistance === "number" && Number.isFinite(w.totalDistance)
      ? w.totalDistance
      : null;
    // Velocidade média só quando dá para calcular de verdade. Distância sem
    // duração viraria Infinity e o gráfico de ritmo mostraria uma barra que
    // some com todas as outras.
    const avgSpeed = distance !== null && durationSec > 0
      ? Math.round((distance / durationSec) * 1000) / 1000
      : null;

    // Sem platformId não há chave estável para deduplicar; o par
    // início+tipo é o mais próximo disso e é estável entre syncs.
    const externalId = w.platformId && w.platformId.length > 0
      ? w.platformId
      : String(w.workoutType || "workout") + ":" + String(startMs);

    rows.push({
      user_id: userId,
      name: humanizeWorkoutType(String(w.workoutType || "")),
      exercises: [{
        type: String(w.workoutType || "other"),
        distance_meters: distance,
        average_speed_mps: avgSpeed,
      }],
      started_at: new Date(startMs).toISOString(),
      completed_at: endIso,
      duration_minutes: Math.max(0, Math.round(durationSec / 60)),
      calories_burned: typeof w.totalEnergyBurned === "number" && Number.isFinite(w.totalEnergyBurned)
        ? Math.round(w.totalEnergyBurned)
        : null,
      source: source,
      external_id: externalId,
    });
  }
  return rows;
}

// ── Métricas (frequência cardíaca, HRV, passos) ──────────────────────────

/** Os tipos de métrica que o VYV lê do hub do SO. */
export const METRIC_TYPES = ["heart_rate", "hrv", "steps"] as const;
export type MetricType = (typeof METRIC_TYPES)[number];

/**
 * Buckets diários agregados pelo plugin → linhas de `health_metrics`.
 *
 * Um dia = uma linha por tipo. `recorded_at` é a MEIA-NOITE do dia civil do
 * usuário e `external_id` é `<tipo>:<dia>` — junto com a UNIQUE
 * (user_id, source, external_id) isso torna o sync idempotente: sincronizar
 * duas vezes o mesmo dia atualiza a mesma linha em vez de duplicar.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * O BUG DE 04/08/2026 — POR QUE O SNAP PARA MEIA-NOITE É OBRIGATÓRIO AQUI
 * ═══════════════════════════════════════════════════════════════════════
 * Primeira e única linha que o Android 1.2.0 gravou, medida em produção:
 *
 *   value 19 · external_id 'steps:2026-08-03'
 *   recorded_at 2026-08-03 19:07:13.835+00
 *   created_at  2026-08-04 19:07:14.014+00
 *
 * O usuário caminhou em 04/08. O dado saiu carimbado com 03/08.
 *
 * A causa NÃO era conversão de fuso — era o instante entregue ao conversor.
 * `queryAggregated({ bucket: "day" })` do Health Connect fatia a partir do
 * INÍCIO DA JANELA DE CONSULTA, em passos fixos de 24h: bucket_n começa em
 * `windowStart + n·24h`. Com `windowStart = agora − 30 dias`, o bucket que
 * cobre "as últimas 24 horas" começa ONTEM, no horário do sync. Confere na
 * casa do milissegundo:
 *
 *   windowStart = 2026-07-05 19:07:13.835Z
 *   bucket 29   = windowStart + 29d = 2026-08-03 19:07:13.835Z ← recorded_at
 *
 * O `.835` é a prova: fronteira de meia-noite de verdade termina em `.000`.
 * `localDayString` fez o certo com o instante errado.
 *
 * POR QUE ISSO ERA P0 E NÃO COSMÉTICO: a migration deste PR troca a dedup
 * para UNIQUE (user_id, source, external_id), com a DATA DENTRO da chave.
 * Sem este snap, o mesmo dia real casaria SEMPRE com a mesma linha errada e
 * seria atualizado no lugar dela — o erro deixaria de ser corrigível por
 * reprocessamento. A migration não causa o bug; ela o cimentaria.
 *
 * DEFESA EM DUAS CAMADAS, de propósito. A janela de consulta passou a ser
 * alinhada à meia-noite local (useConnections.ts), o que já faria os buckets
 * caírem certos — mas isso depende de o plugin honrar a fronteira pedida, e
 * o horário de verão move essa fronteira uma vez por semestre. O snap aqui
 * é puro, testável, e vale mesmo se o plugin devolver outra coisa.
 */
export function buildMetricRows(
  samples: RawAggregatedSample[],
  userId: string,
  source: HealthProvider,
  metricType: MetricType,
  unit: string,
  /** Fuso do usuário (`user_whisper_preferences.timezone`). */
  tz: string,
): MetricRow[] {
  const rows: MetricRow[] = [];
  for (const s of samples) {
    if (!s || typeof s.startDate !== "string") continue;
    if (typeof s.value !== "number" || !Number.isFinite(s.value)) continue;
    // Zero é dado legítimo para passos (dia parado), mas negativo não existe
    // em nenhuma das três métricas — descartar em vez de gravar lixo.
    if (s.value < 0) continue;
    const startMs = new Date(s.startDate).getTime();
    if (!Number.isFinite(startMs)) continue;

    // Fim da janela do bucket. Um bucket diário cobre um dia inteiro, e sem o
    // fim "passos de 03/08" e "passos das 00h de 03/08" são indistinguíveis —
    // os rollups do PR 2 não conseguem somar duração nem decidir a que dia uma
    // janela que cruza a meia-noite pertence. endDate inválido vira null
    // (pontual) em vez de derrubar a linha: perder a janela é ruim, perder a
    // amostra é pior.
    const endMs = typeof s.endDate === "string" ? new Date(s.endDate).getTime() : NaN;

    // O snap: da fronteira que o plugin devolveu para a meia-noite do dia
    // civil do usuário. `dayStart` manda em recorded_at E em external_id, para
    // os dois nunca discordarem sobre a que dia a linha pertence.
    const dayStart = startOfDayInZoneUtc(new Date(startMs), tz);
    const dayString = dayStringInZone(dayStart, tz);
    // O fim vira o começo do dia SEGUINTE quando o plugin não deu um fim
    // utilizável — bucket diário cobre o dia inteiro. Recalculado por
    // `startOfDayInZoneUtc` em vez de somar 86.400.000: no dia da virada do
    // horário de verão o dia civil tem 23h ou 25h, e somar 24h fixo colocaria
    // `ended_at` dentro do dia errado duas vezes por ano.
    const nextDayStart = startOfDayInZoneUtc(
      new Date(dayStart.getTime() + 36 * 3600 * 1000),
      tz,
    );
    const endedAt = Number.isFinite(endMs) && endMs > dayStart.getTime()
      ? new Date(Math.min(endMs, nextDayStart.getTime())).toISOString()
      : nextDayStart.toISOString();

    rows.push({
      user_id: userId,
      metric_type: metricType,
      value: Math.round(s.value * 100) / 100,
      unit: unit,
      recorded_at: dayStart.toISOString(),
      ended_at: endedAt,
      source: source,
      // ⚠️ O TIPO ENTRA NA CHAVE. A lápide de exclusão é
      // (user_id, table_name, source, external_id) — sem metric_type. Se os
      // três tipos do mesmo dia compartilhassem o external_id "2026-03-11",
      // apagar SÓ os passos criaria uma lápide que também suprimiria
      // frequência cardíaca e HRV daquele dia para sempre, silenciosamente.
      // E, pior, a segunda exclusão do mesmo dia bateria na chave única da
      // lápide.
      external_id: metricType + ":" + dayString,
      metadata: null,
    });
  }
  return rows;
}

/**
 * Agrega samples crus em buckets DIÁRIOS locais.
 *
 * Existe porque `queryAggregated` NÃO cobre tudo nos dois sistemas: o Health
 * Connect lança para heartRateVariability, respiratoryRate, oxygenSaturation e
 * vo2Max — está escrito no próprio plugin ("Aggregated queries are not
 * supported for {tipo}. Use readSamples instead.", HealthManager.kt:903). Sem
 * este caminho, HRV no Android falharia em TODO sync e o status da conexão
 * ficaria eternamente 'partial'.
 *
 * Agregar aqui em vez de ramificar por plataforma no chamador mantém o
 * comportamento igual nos dois sistemas — e testável.
 */
export function aggregateSamplesByDay(
  samples: { startDate: string; value: number }[],
  mode: "sum" | "average",
  /** Fuso do usuário — o mesmo de buildMetricRows, para os dois caminhos
   *  (queryAggregated e readSamples) concordarem sobre o que é "um dia". */
  tz: string,
): RawAggregatedSample[] {
  const byDay = new Map<string, { sum: number; count: number; startMs: number }>();
  for (const s of samples) {
    if (!s || typeof s.startDate !== "string") continue;
    if (typeof s.value !== "number" || !Number.isFinite(s.value)) continue;
    const d = new Date(s.startDate);
    const ms = d.getTime();
    if (!Number.isFinite(ms)) continue;
    const day = dayStringInZone(d, tz);
    const cur = byDay.get(day);
    if (cur) {
      cur.sum += s.value;
      cur.count += 1;
      if (ms < cur.startMs) cur.startMs = ms;
    } else {
      byDay.set(day, { sum: s.value, count: 1, startMs: ms });
    }
  }

  const out: RawAggregatedSample[] = [];
  for (const [day, agg] of byDay) {
    // Meia-noite do dia civil do USUÁRIO, não do aparelho, e não o primeiro
    // sample: é o que torna o upsert idempotente entre syncs (o primeiro
    // sample do dia muda conforme o relógio sincroniza). `setHours(0,0,0,0)`
    // usava o fuso do aparelho — mesma classe do bug de 04/08, só que por
    // outra porta.
    const midnight = startOfDayInZoneUtc(new Date(agg.startMs), tz);
    const nextMidnight = startOfDayInZoneUtc(
      new Date(midnight.getTime() + 36 * 3600 * 1000),
      tz,
    );
    out.push({
      startDate: midnight.toISOString(),
      endDate: nextMidnight.toISOString(),
      value: mode === "sum" ? agg.sum : agg.sum / agg.count,
    });
    void day;
  }
  out.sort((a, b) => (a.startDate < b.startDate ? -1 : 1));
  return out;
}

// ── Lápide ───────────────────────────────────────────────────────────────

export interface DeletionTombstone {
  table_name: string;
  source: string;
  external_id: string;
}

/**
 * Remove das linhas a serem gravadas tudo o que o usuário JÁ APAGOU.
 *
 * Sem isto, apagar um dado importado é teatro: some da tela e volta no próximo
 * sync. É a "regra 30/06" (exclusão é respeitada) aplicada ao sync nativo —
 * a mesma que google-cal-pull implementa via `deleted_at` em agenda_events.
 * Aqui a lápide mora em `integration_deletions` porque sleep_logs e
 * workout_sessions não têm coluna de tombstone e somar uma obrigaria os 60
 * sites que leem essas tabelas a filtrar.
 */
/**
 * Origens que vêm de integração — as únicas cujo DELETE precisa de lápide.
 *
 * Mora aqui, e não no deleteImported.ts, por um motivo prático: este módulo
 * não importa nada (nem o client do Supabase), então o vitest consegue testá-lo
 * direto. É o mesmo motivo pelo qual pageRows.ts e chunkIds.ts são sem imports.
 */
const EXTERNAL_SOURCES = new Set([
  "apple_health", "health_connect", "google_health", "google_fit",
  "strava", "oura", "fitbit", "garmin", "whoop", "wearable",
  "spotify", "notion", "google_calendar", "google_tasks", "outlook", "apple_calendar",
]);

/**
 * `true` quando a linha veio de fora e portanto voltaria num sync.
 *
 * 'voice' e 'mirror' NÃO estão na lista: são dado que o usuário produziu
 * (falando em vez de digitando), nenhum sync os regenera, e tratá-los como
 * externos foi um bug real — o gate antigo era `source !== 'manual'`, o que
 * deixava uma noite registrada por voz sem botão de apagar.
 */
export function isExternalSource(source: string | null | undefined): boolean {
  if (!source) return false;
  return EXTERNAL_SOURCES.has(source);
}

export function filterTombstoned<T extends { external_id: string; source: string }>(
  rows: T[],
  tombstones: DeletionTombstone[],
  tableName: string,
): T[] {
  if (tombstones.length === 0) return rows;
  const dead = new Set<string>();
  for (const t of tombstones) {
    if (t.table_name === tableName) dead.add(t.source + "\u0001" + t.external_id);
  }
  if (dead.size === 0) return rows;
  return rows.filter((r) => !dead.has(r.source + "\u0001" + r.external_id));
}
